import React, { Component } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import Multiselect from "multiselect-react-dropdown";

import { getActiveSpecialGroups, getStudentSpecialGroups, assignStudentSpecialGroup, unassignStudentSpecialGroup } from "./apis";
import "./Edit.css";

// Student special-group assignment. Replaces the legacy GAP/Honorific/Accelerate checkboxes.
// Active groups are managed through the multiselect (immediate assign/unassign so each change is
// independently audited); groups that have since been deactivated remain visible as read-only,
// greyed "(inactive)" chips that cannot be removed or re-added.
class StudentSpecialGroups extends Component {
    state = {
        activeGroups: [],
        selectedActive: [],
        inactiveAssigned: [],
        saving: false,
        loaded: false,
    };

    componentDidMount = async () => {
        this._isMounted = true;
        try {
            const [activeResp, assignedResp] = await Promise.all([
                getActiveSpecialGroups(),
                getStudentSpecialGroups(this.props.userProfileId),
            ]);

            const activeGroups = activeResp.data.result || [];
            const assigned = assignedResp.data.result || [];

            // Use the option references from activeGroups for the selected values so the dropdown
            // reliably hides already-assigned active groups.
            const assignedActiveIds = assigned.filter((g) => g.isActive).map((g) => g.id);
            const selectedActive = activeGroups.filter((g) => assignedActiveIds.includes(g.id));
            const inactiveAssigned = assigned.filter((g) => !g.isActive);

            if (this._isMounted) {
                this.setState({ activeGroups, selectedActive, inactiveAssigned, loaded: true });
            }
        } catch (err) {
            toast.error("Could not load special groups.");
            if (this._isMounted) {
                this.setState({ loaded: true });
            }
        }
    };

    componentWillUnmount = () => {
        this._isMounted = false;
    };

    onSelect = async (selectedList, selectedItem) => {
        // Optimistically reflect the selection so the chip shows immediately; show a spinner while
        // the assignment persists rather than disabling the whole dropdown (which left it visually stuck).
        this.setState({ selectedActive: selectedList, saving: true });
        try {
            await assignStudentSpecialGroup({
                userProfileId: this.props.userProfileId,
                specialGroupId: selectedItem.id,
            });
        } catch (err) {
            toast.error("Could not assign special group.");
            // Roll back the optimistic add on failure.
            this.setState({ selectedActive: selectedList.filter((g) => g.id !== selectedItem.id) });
        } finally {
            if (this._isMounted) {
                this.setState({ saving: false });
            }
        }
    };

    onRemove = async (selectedList, removedItem) => {
        this.setState({ selectedActive: selectedList, saving: true });
        try {
            await unassignStudentSpecialGroup({
                userProfileId: this.props.userProfileId,
                specialGroupId: removedItem.id,
            });
        } catch (err) {
            toast.error("Could not remove special group.");
            // Roll back the optimistic removal on failure.
            this.setState({ selectedActive: [...selectedList, removedItem] });
        } finally {
            if (this._isMounted) {
                this.setState({ saving: false });
            }
        }
    };

    render() {
        if (!this.state.loaded) {
            return <div className="specialGroupLoading">Loading special groups...</div>;
        }

        const { activeGroups, selectedActive, inactiveAssigned, saving } = this.state;

        return (
            <div className="studentSpecialGroups">
                <span className="studentSpecialGroupsLabel">
                    Special Groups
                    {saving && (
                        <>
                            <span className="specialGroupSpinner" aria-label="Saving" />
                            <span className="specialGroupSavingText">Saving...</span>
                        </>
                    )}
                </span>
                <Multiselect
                    options={activeGroups}
                    selectedValues={selectedActive}
                    onSelect={this.onSelect}
                    onRemove={this.onRemove}
                    displayValue="name"
                    className="AdvisorSchoolSelector"
                    placeholder="Add a special group..."
                    disable={this.props.readonly}
                />
                {inactiveAssigned.length > 0 && (
                    <div className="specialGroupInactiveChips">
                        {inactiveAssigned.map((group) => (
                            <span className="specialGroupInactiveChip" key={group.id}>
                                {group.name} <em>(inactive)</em>
                            </span>
                        ))}
                    </div>
                )}
            </div>
        );
    }
}

StudentSpecialGroups.propTypes = {
    userProfileId: PropTypes.string.isRequired,
    readonly: PropTypes.bool,
};

StudentSpecialGroups.defaultProps = {
    readonly: false,
};

export default StudentSpecialGroups;
